import React from 'react';
import styled from 'styled-components';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { IPipelineSummary } from '../../../core/modules/pipelines/pipelines.model';
import { BRENDA_GREEN } from '../utils/statusColors';

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
`;

const Tile = styled.div<{ $accent: string }>`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  border-left: 3px solid ${({ $accent }) => $accent};
`;

const IconBox = styled.div<{ $accent: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $accent }) => `${$accent}15`};
  color: ${({ $accent }) => $accent};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Number = styled.div`
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  color: rgba(0, 0, 0, 0.85);
  font-variant-numeric: tabular-nums;
`;

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 4px;
`;

interface IOverviewStatsProps {
  summaries: IPipelineSummary[];
}

const OverviewStats: React.FC<IOverviewStatsProps> = ({ summaries }) => {
  const counts = summaries.reduce(
    (acc, p) => {
      if (p.latestRunStatus === 'running' || p.latestRunStatus === 'pending') {
        acc.running += 1;
      } else if (p.latestRunStatus === 'success') {
        acc.healthy += 1;
      } else if (p.latestRunStatus === 'failed' || p.latestRunStatus === 'cancelled') {
        acc.failed += 1;
      }

      acc.total += 1;

      return acc;
    },
    { running: 0, healthy: 0, failed: 0, total: 0 }
  );

  return (
    <Row>
      <Tile $accent='#1890ff'>
        <IconBox $accent='#1890ff'><SyncOutlined /></IconBox>
        <Body>
          <Number>{counts.running}</Number>
          <Label>Running</Label>
        </Body>
      </Tile>
      <Tile $accent={BRENDA_GREEN}>
        <IconBox $accent={BRENDA_GREEN}><CheckCircleOutlined /></IconBox>
        <Body>
          <Number>{counts.healthy}</Number>
          <Label>Healthy</Label>
        </Body>
      </Tile>
      <Tile $accent='#ff4d4f'>
        <IconBox $accent='#ff4d4f'><CloseCircleOutlined /></IconBox>
        <Body>
          <Number>{counts.failed}</Number>
          <Label>Failed</Label>
        </Body>
      </Tile>
      <Tile $accent='#8c8c8c'>
        <IconBox $accent='#8c8c8c'><AppstoreOutlined /></IconBox>
        <Body>
          <Number>{counts.total}</Number>
          <Label>Total</Label>
        </Body>
      </Tile>
    </Row>
  );
};

export default OverviewStats;
